import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import { BrakeSortBy } from '@/features/brakes/hooks/useBrakesQueryParams';
import { CommonSortBy } from '@/features/common/hooks/useCommonQueryParams';
import { SortDirection } from '@/features/common/hooks/useSorting';
import {
    GroupedBrakePageResponse,
    MaintenanceComponent,
    MaintenanceComponentPatchRequest,
    MaintenanceDataPageResponse,
    MaintenanceDataSearchRequest,
} from '@/generated/GeneratedApiTypes';

export type MaintenanceSortBy = NonNullable<MaintenanceDataSearchRequest['sort_by']>;

export interface CommonQueryParams<S = CommonSortBy> {
    pageSize: number | undefined | null;
    pageNumber: number | undefined | null;
    sortBy: S | undefined | null;
    sortDirection: SortDirection | undefined | null;
    assetIds: string[] | undefined | null;
    workshopIds: string[] | undefined | null;
    accountIds: string[] | undefined | null;
}

export interface GetBrakesRequestParams extends CommonQueryParams<BrakeSortBy> {
    min: number | undefined;
    max: number | undefined;
    status: string[] | undefined;
}

export interface MaintenanceDataRequestParams extends CommonQueryParams<MaintenanceSortBy> {
    status: MaintenanceDataSearchRequest['status'];
    componentType: MaintenanceDataSearchRequest['component_type'];
    component: string[] | undefined | null;
    dueDateFrom: string | undefined | null;
    dueDateTo: string | undefined | null;
    dueMileageMin: number | undefined | null;
    dueMileageMax: number | undefined | null;
    mileageMin: number | undefined | null;
    mileageMax: number | undefined | null;
    dueRangesOperator: MaintenanceDataSearchRequest['due_ranges_operator'];
    activeMaintenance: boolean | undefined | null;
    solvedByCustomer: boolean | undefined | null;
}

export const API_PATH_PREFIX = '/vehicle-diagnostics';
const filenameRegexMatcher = /filename="(.+\.[a-z]{2,})"/gi;

const arrayOrUndefined = <T>(arr: T[] | undefined | null) => (arr && arr.length > 0 ? arr : undefined);

const csvDownloadResponseHandler = async (response: Response) => {
    try {
        if (!response.ok) {
            return {
                isSuccess: false,
                reason: response.statusText,
            };
        }
        const defaultFilename = `${new Date().toISOString().split('.')[0]}.csv`;

        const data = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition') ?? 'NO_HEADER';
        const filename = filenameRegexMatcher.exec(contentDisposition)?.[1] ?? defaultFilename;

        const url = window.URL.createObjectURL(data);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
        return {
            isSuccess: true,
        };
    } catch (e) {
        return {
            isSuccess: false,
        };
    }
};

const convertCommonParams = (params: CommonQueryParams) => ({
    page_number: params.pageNumber || undefined,
    page_size: params.pageSize || undefined,
    sort_by: params.sortBy || undefined,
    sort_direction: params.sortDirection || undefined,
    asset_ids: arrayOrUndefined(params.assetIds),
    workshop_ids: arrayOrUndefined(params.workshopIds),
    account_ids: arrayOrUndefined(params.accountIds),
});

const toMaintenanceSearchBody = (params: MaintenanceDataRequestParams): MaintenanceDataSearchRequest => ({
    ...convertCommonParams(params as CommonQueryParams),
    sort_by: params.sortBy || undefined,
    status: arrayOrUndefined(params.status),
    component_type: arrayOrUndefined(params.componentType),
    component: arrayOrUndefined(params.component),
    due_date_from: params.dueDateFrom || undefined,
    due_date_to: params.dueDateTo || undefined,
    due_mileage_min: params.dueMileageMin ?? undefined,
    due_mileage_max: params.dueMileageMax ?? undefined,
    mileage_min: params.mileageMin ?? undefined,
    mileage_max: params.mileageMax ?? undefined,
    due_ranges_operator: params.dueRangesOperator || undefined,
    active_maintenance: params.activeMaintenance ? true : undefined,
    solved_by_customer: params.solvedByCustomer ? true : undefined,
});

export const vehicleDiagnosticsApi = createApi({
    reducerPath: 'vehicleDiagnosticsApi',
    baseQuery: fetchBaseQuery({ baseUrl: `${import.meta.env.VITE_BACKEND_URI}`, prepareHeaders }),
    tagTypes: ['MaintenanceData'],
    endpoints: (builder) => ({
        getGroupedBrakes: builder.query<GroupedBrakePageResponse, GetBrakesRequestParams>({
            query: (allParams: GetBrakesRequestParams) => {
                const convertedParams = {
                    ...convertCommonParams(allParams as CommonQueryParams),
                    min: allParams.min || undefined,
                    max: allParams.max || undefined,
                    status: arrayOrUndefined(allParams.status),
                };
                return {
                    url: `${API_PATH_PREFIX}/brakes/grouped`,
                    params: { ...convertedParams },
                };
            },
        }),
        downloadCsvFile: builder.mutation({
            query: (allParams: GetBrakesRequestParams) => ({
                url: `${API_PATH_PREFIX}/export/brakes`,
                params: {
                    ...convertCommonParams(allParams as CommonQueryParams),
                    min: allParams.min || undefined,
                    max: allParams.max || undefined,
                    status: arrayOrUndefined(allParams.status),
                },
                responseHandler: csvDownloadResponseHandler,
            }),
        }),
        searchMaintenanceData: builder.query<MaintenanceDataPageResponse, MaintenanceDataRequestParams>({
            query: (allParams: MaintenanceDataRequestParams) => ({
                url: `${API_PATH_PREFIX}/maintenance-data`,
                method: 'POST',
                body: toMaintenanceSearchBody(allParams),
            }),
            providesTags: ['MaintenanceData'],
        }),
        patchMaintenanceComponent: builder.mutation<
            MaintenanceComponent,
            { id: string; body: MaintenanceComponentPatchRequest }
        >({
            query: ({ id, body }) => ({
                url: `${API_PATH_PREFIX}/maintenance-components/${id}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['MaintenanceData'],
        }),
        downloadMaintenanceDataCsvFile: builder.mutation({
            query: (allParams: MaintenanceDataRequestParams) => ({
                url: `${API_PATH_PREFIX}/export/maintenance-data`,
                method: 'POST',
                body: toMaintenanceSearchBody(allParams),
                responseHandler: csvDownloadResponseHandler,
            }),
        }),
    }),
});

export const {
    useGetGroupedBrakesQuery,
    useDownloadCsvFileMutation,
    useSearchMaintenanceDataQuery,
    usePatchMaintenanceComponentMutation,
    useDownloadMaintenanceDataCsvFileMutation,
} = vehicleDiagnosticsApi;
